import { create } from 'zustand';
import { locationService, NearbyLocationsParams } from '../services/locationService';
import { BackendLocation } from '../types/location';
import { ChargingLocation } from '../types';
import { transformLocations } from '../utils/transformers';
import { handleApiError, formatErrorForDisplay, captureError } from '../lib/errorHandler';

interface LocationState {
  // State
  locations: ChargingLocation[]; // Transformed UI locations
  rawLocations: BackendLocation[]; // Original backend data
  selectedLocationId: string | null;
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;
  userLocation: { latitude: number; longitude: number; accuracy?: number } | null;

  // Actions
  setUserLocation: (latitude: number, longitude: number, accuracy?: number) => void;
  fetchNearbyLocations: (params: NearbyLocationsParams) => Promise<void>;
  fetchLocationById: (id: string) => Promise<ChargingLocation | null>;
  setSelectedLocationId: (id: string | null) => void;
  clearError: () => void;
  reset: () => void;
}

/**
 * Location Store
 * Manages location state and fetching from API
 */
export const useLocationStore = create<LocationState>((set, get) => ({
  // Initial state
  locations: [],
  rawLocations: [],
  selectedLocationId: null,
  isLoading: false,
  error: null,
  lastFetched: null,
  userLocation: null,

  // Set user location
  setUserLocation: (latitude: number, longitude: number, accuracy?: number) => {
    set({
      userLocation: { latitude, longitude, accuracy },
    });
  },

  // Fetch nearby locations
  fetchNearbyLocations: async (params: NearbyLocationsParams) => {
    set({ isLoading: true, error: null });

    try {
      console.log('[LocationStore] Fetching nearby locations:', params);

      const rawLocations = await locationService.getNearbyLocations(params);

      console.log(`[LocationStore] Fetched ${rawLocations.length} locations`);

      // Transform to UI format
      const locations = transformLocations(rawLocations);

      set({
        locations,
        rawLocations,
        isLoading: false,
        error: null,
        lastFetched: Date.now(),
      });
    } catch (error) {
      captureError(error, 'LocationStore.fetchNearbyLocations');

      const appError = handleApiError(error);

      set({
        isLoading: false,
        error: formatErrorForDisplay(appError),
      });

      throw error;
    }
  },

  // Fetch a single location by ID (for deep-linking)
  fetchLocationById: async (id: string) => {
    try {
      console.log('[LocationStore] Fetching location by ID:', id);

      const rawLocation = await locationService.getLocationById(id);
      const [transformed] = transformLocations([rawLocation]);

      // Add to locations if not already present
      const currentLocations = get().locations;
      const currentRawLocations = get().rawLocations;

      if (!currentLocations.find(l => l.id === id)) {
        set({
          locations: [...currentLocations, transformed],
          rawLocations: [...currentRawLocations, rawLocation],
        });
      }

      return transformed;
    } catch (error) {
      captureError(error, 'LocationStore.fetchLocationById', { id });
      const appError = handleApiError(error);
      throw appError; // Let caller handle the error
    }
  },

  // Set selected location ID
  setSelectedLocationId: (id: string | null) => {
    set({ selectedLocationId: id });
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },

  // Reset store to initial state
  reset: () => {
    set({
      locations: [],
      rawLocations: [],
      selectedLocationId: null,
      isLoading: false,
      error: null,
      lastFetched: null,
      userLocation: null,
    });
  },
}));
